import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X } from "lucide-react";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
    <div className="w-10 h-10 rounded-xl bg-[#8B5A3C] flex items-center justify-center text-white font-display text-lg font-semibold shadow-sm group-hover:bg-[#704830] transition-colors">
      LS
    </div>
    <div className="leading-tight">
      <div className="font-display text-lg font-semibold text-[#2C2621]">La Sala</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8178] -mt-0.5">Immobiliare</div>
    </div>
  </Link>
);

const Header = () => {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const linkBase = "text-sm font-medium transition-colors";
  const linkClass = ({ isActive }) =>
    `${linkBase} ${isActive ? "text-[#8B5A3C]" : "text-[#2C2621] hover:text-[#8B5A3C]"}`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#E8DFD8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
          <NavLink to="/" className={linkClass} end data-testid="nav-home">{t.nav.home}</NavLink>
          <NavLink to="/immobili" className={linkClass} data-testid="nav-properties">{t.nav.properties}</NavLink>
          <NavLink to="/chi-siamo" className={linkClass} data-testid="nav-about">{t.nav.about}</NavLink>
          <NavLink to="/contatti" className={linkClass} data-testid="nav-contact">{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-[#5C544B] hover:text-[#8B5A3C] transition-colors px-2 py-1"
            aria-label="Toggle language"
          >
            <span className={lang === "it" ? "text-[#8B5A3C]" : ""}>IT</span>
            <span className="text-[#E8DFD8]">/</span>
            <span className={lang === "en" ? "text-[#8B5A3C]" : ""}>EN</span>
          </button>

          <Link
            to="/valutazione"
            data-testid="header-evaluation-cta"
            className="hidden lg:inline-flex bg-[#8B5A3C] text-white hover:bg-[#704830] transition-colors rounded-full px-6 py-2.5 text-sm font-medium"
          >
            {t.nav.evaluation}
          </Link>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E8DFD8] bg-[#FDFBF7]" data-testid="mobile-nav">
          <nav className="px-6 py-6 flex flex-col gap-5">
            <NavLink to="/" className={linkClass} end>{t.nav.home}</NavLink>
            <NavLink to="/immobili" className={linkClass}>{t.nav.properties}</NavLink>
            <NavLink to="/chi-siamo" className={linkClass}>{t.nav.about}</NavLink>
            <NavLink to="/contatti" className={linkClass}>{t.nav.contact}</NavLink>
            <Link to="/valutazione" className="bg-[#8B5A3C] text-white rounded-full px-6 py-2.5 text-sm font-medium text-center">
              {t.nav.evaluation}
            </Link>
            <button onClick={toggle} className="self-start text-[11px] font-bold uppercase tracking-[0.22em] text-[#5C544B]">
              <span className={lang === "it" ? "text-[#8B5A3C]" : ""}>IT</span>
              {" / "}
              <span className={lang === "en" ? "text-[#8B5A3C]" : ""}>EN</span>
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
